import { CardShell, FIELD_LABEL_CLASS } from '@atlas/ui-components'
import { Button, Label } from '@atlas/ui-primitives'
import type { FormEvent } from 'react'
import { ClassifyPicker, facetsToRailAxes } from '@/components/classification/ClassifyPicker'
import type { Facet } from '@/domain/library.types'
import { type CreateRuleInput, RuleValidationMethod } from '@/domain/rules.types'
import { AppliesToPicker } from './AppliesToPicker'
import { RuleTypeSeverityFields } from './RuleTypeSeverityFields'
import { RuleValidationFields } from './RuleValidationFields'

export interface CreateRuleFormProps {
	value: CreateRuleInput
	facets: Facet[]
	onChange: (next: CreateRuleInput) => void
	onSubmit: () => void
	onCancel: () => void
	submitting?: boolean
}

const fieldLabel = `mb-1.5 block ${FIELD_LABEL_CLASS}`

/** Create-rule form (design §8.13 `rule_new`). Controlled; submits via native form. */
export function CreateRuleForm({ value, facets, onChange, onSubmit, onCancel, submitting }: CreateRuleFormProps) {
	function submit(e: FormEvent) {
		e.preventDefault()
		onSubmit()
	}

	return (
		<CardShell as="form" onSubmit={submit} className="max-w-2xl p-6">
			<div className="mb-4">
				<Label htmlFor="statement" className={fieldLabel}>
					Statement
				</Label>
				<textarea
					id="statement"
					required
					rows={2}
					value={value.statement}
					onChange={(e) => onChange({ ...value, statement: e.target.value })}
					placeholder="e.g. Duration seal must read the market-approved wording…"
					className="w-full rounded-ds-md border border-ds-border bg-ds-background px-3 py-2 text-sm text-ds-text"
				/>
			</div>

			<div className="mb-4">
				<Label htmlFor="check-definition" className={fieldLabel}>
					Check definition
				</Label>
				<textarea
					id="check-definition"
					rows={2}
					value={value.checkDefinition ?? ''}
					onChange={(e) => onChange({ ...value, checkDefinition: e.target.value })}
					placeholder="e.g. Read the duration seal and compare it word-for-word to the approved wording…"
					className="w-full rounded-ds-md border border-ds-border bg-ds-background px-3 py-2 text-sm text-ds-text"
				/>
				<p className="mt-1 text-xs text-ds-text-muted">
					The instruction the validation agent runs. A rule without one cannot be activated, and reports as uncheckable
					when validated.
				</p>
			</div>

			<div className="mb-4">
				<RuleTypeSeverityFields
					type={value.type}
					severity={value.severity}
					idPrefix="create"
					onChange={(next) => onChange({ ...value, ...next })}
				/>
			</div>

			<div className="mb-4">
				<span className={fieldLabel}>Classify — applies to (empty = all)</span>
				<ClassifyPicker
					value={value.classification}
					axes={facetsToRailAxes(facets)}
					onChange={(classification) => onChange({ ...value, classification })}
				/>
			</div>

			<div className="mb-4">
				<RuleValidationFields
					validationMethod={value.validationMethod ?? RuleValidationMethod.MULTIMODAL_LLM}
					metadataCheck={value.metadataCheck ?? null}
					facets={facets}
					idPrefix="create"
					onChange={(next) => onChange({ ...value, ...next })}
				/>
			</div>

			<div className="mb-6">
				<span className={fieldLabel}>Governs element types (empty = all)</span>
				<AppliesToPicker
					value={value.appliesTo ?? []}
					onChange={(appliesTo) => onChange({ ...value, appliesTo })}
					idPrefix="create-applies-to"
				/>
			</div>

			<div className="flex gap-2">
				<Button type="submit" isDisabled={submitting} className="rounded-ds-full px-5">
					{submitting ? 'Creating…' : 'Create rule'}
				</Button>
				<Button type="button" variant="secondary" className="rounded-ds-full" onPress={onCancel}>
					Cancel
				</Button>
			</div>
		</CardShell>
	)
}
